import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Loader2 } from "lucide-react";
import { downloadPdf } from "@/lib/api";

const STATUS_CHOICES = [
  { key: "shadowed", label: "Shadowed" },
  { key: "redundant", label: "Redundant" },
  { key: "overlap", label: "Overlap" },
  { key: "clean", label: "Clean" },
];

export const ReportDialog = ({ open, onOpenChange, analysis }) => {
  const [title, setTitle] = useState("Shadow Firewall Policy — Audit Report");
  const [orientation, setOrientation] = useState("landscape");
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeFindings, setIncludeFindings] = useState(true);
  const [includeAllRules, setIncludeAllRules] = useState(true);
  const [statuses, setStatuses] = useState({
    shadowed: true,
    redundant: true,
    overlap: true,
    clean: false,
  });
  const [loading, setLoading] = useState(false);

  const toggleStatus = (k) => setStatuses((s) => ({ ...s, [k]: !s[k] }));

  const submit = async () => {
    setLoading(true);
    try {
      await downloadPdf(analysis, {
        title,
        orientation,
        include_summary: includeSummary,
        include_findings: includeFindings,
        include_all_rules: includeAllRules,
        statuses: STATUS_CHOICES.filter((c) => statuses[c.key]).map((c) => c.key),
      });
      toast.success("PDF report downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="report-dialog"
        className="bg-[#0f172a] border-[#1e293b] rounded-sm max-w-2xl"
      >
        <DialogHeader>
          <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-blue-400 mb-1">
            pdf_report
          </div>
          <DialogTitle className="text-slate-50 text-xl">
            Customize your audit report
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400 mt-2">
            Pick which sections and which statuses appear in the PDF. Layout stays consistent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400">
              Report title
            </Label>
            <Input
              data-testid="report-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
            />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400">
              Page orientation
            </Label>
            <Select value={orientation} onValueChange={setOrientation}>
              <SelectTrigger
                data-testid="report-orientation-select"
                className="mt-2 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-[#1e293b] rounded-sm">
                <SelectItem value="landscape">Landscape A4</SelectItem>
                <SelectItem value="portrait">Portrait A4</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400 mb-3">
              Sections
            </div>
            <div className="space-y-2 text-sm text-slate-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="report-include-summary"
                  checked={includeSummary}
                  onCheckedChange={setIncludeSummary}
                />
                Summary metrics
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="report-include-findings"
                  checked={includeFindings}
                  onCheckedChange={setIncludeFindings}
                />
                Findings table (issues only)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="report-include-allrules"
                  checked={includeAllRules}
                  onCheckedChange={setIncludeAllRules}
                />
                All-rules appendix
              </label>
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400 mb-3">
              Findings — which statuses to include
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-slate-200">
              {STATUS_CHOICES.map((c) => (
                <label key={c.key} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid={`report-status-${c.key}`}
                    checked={statuses[c.key]}
                    onCheckedChange={() => toggleStatus(c.key)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm border-[#1e293b] bg-transparent hover:bg-[#1e293b] text-slate-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="report-generate-btn"
            onClick={submit}
            disabled={loading}
            className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
